import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';

// Same focusable selector as SideDrawer's focus trap.
const FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface ModalA11yOptions {
    dialogRef: RefObject<HTMLElement>;
    /** Where focus returns on close — the button that opened the modal. */
    triggerRef: RefObject<HTMLElement>;
    onClose: () => void;
}

/**
 * Dialog behaviour the shared Modal does not provide: focus in on open, a Tab trap, focus restore on
 * every close path (all of them unmount this), and Escape closing only this modal. The Escape
 * listener is registered in the CAPTURE phase and stops propagation: the SideDrawer underneath
 * listens for Escape on `document` in the bubble phase, so a bubble-phase handler here would close
 * both layers at once.
 */
export function useDataAvailabilityModalA11y({ dialogRef, triggerRef, onClose }: ModalA11yOptions) {
    const onCloseRef = useRef(onClose);
    useEffect(() => {
        onCloseRef.current = onClose;
    }, [onClose]);

    useEffect(() => {
        const dialog = dialogRef.current;
        dialog?.focus();

        const focusableElements = () => (dialog ? Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)) : []);

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                onCloseRef.current();
                return;
            }
            if (e.key !== 'Tab' || !dialog) return;
            const focusable = focusableElements();
            if (!focusable.length) {
                e.preventDefault();
                dialog.focus();
                return;
            }
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            const active = document.activeElement;
            const focusIsInside = dialog.contains(active);
            if (!focusIsInside) {
                e.preventDefault();
                (e.shiftKey ? last : first).focus();
            } else if (e.shiftKey && (active === first || active === dialog)) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && active === last) {
                e.preventDefault();
                first.focus();
            }
        };

        document.addEventListener('keydown', handleKeyDown, true);
        return () => {
            document.removeEventListener('keydown', handleKeyDown, true);
            // Read at close, not open: a re-fit may have swapped the trigger for the ⋯ menu meanwhile.
            triggerRef.current?.focus();
        };
    }, []);
}
